import { Scene, SceneExtended } from "./types";

/**
 * Whether the Configurator's plan mode (drawing/editing walls & rooms) can be
 * entered for this scene. Only scenes authored via the in-app Floorplanner or
 * the roomscanner-floorplanner pipeline carry a linked floorplan
 * (`scene.plan.floorplanId`) — model-import scenes have no floorplan-authored
 * architecture for plan mode to operate on.
 *
 * This gates scene-level plan-mode *entry* only, not per-object architecture
 * editability. Once a scene can mix imported and dynamically-created
 * architecture, which individual objects are editable inside plan mode is a
 * separate, already-existing per-object concept — see `isFloorplanArchitecture`
 * and the `origin: 'floorplan.r3f'` marker in sceneParser.ts /
 * buildArchitectureEntries.ts.
 */
export const isPlanModeAvailable = (
	scene: Scene | SceneExtended | undefined | null,
): boolean => {
	const floorplanId = scene?.plan?.floorplanId;
	if (!floorplanId) return false;
	return typeof floorplanId === "object" ? !!floorplanId._id : !!floorplanId;
};

/**
 * Whether the scene carries a thumbnail of its own, as opposed to the
 * placeholder every scene is born with.
 *
 * `meta.thumbnail` is effectively never empty: the scene model defaults it to
 * `'../scene.jpg'`, following the same `'../'`-prefixed convention as
 * `'../object3d.jpg'` and `'../material.jpg'` (see GalleryItem's DEFAULT_IMAGE).
 * Those paths resolve to a shared default image one directory above the item's
 * own S3 folder — they are a "nothing here" marker, not a stored file.
 *
 * So a plain truthiness check on `meta.thumbnail` answers "yes" for every scene
 * ever created, which is exactly what stopped the first-viewpoint thumbnail
 * convenience from ever firing.
 */
export const hasOwnThumbnail = (
	scene: Scene | SceneExtended | undefined | null,
): boolean => {
	const thumbnail = scene?.meta?.thumbnail;
	if (!thumbnail) return false;
	return !thumbnail.startsWith("../");
};

/**
 * Whether the scene was authored by the RoomScanner app, as opposed to the
 * in-app Floorplanner or a 3D-model import.
 *
 * `plan.roomscanId` is set once, at import time, by hegiasGo's
 * createFromRoomscan.js and never by any other creation path. Scene copies
 * deliberately keep the reference (see scene.utils.js `duplicateLinkedFloorplan`),
 * so a duplicate of a scanned scene still counts as one.
 *
 * The distinction matters because scene attachments — measurements, notes,
 * planviews, PDFs, photos — can only ever enter a scene through the
 * RoomScanner. For every other scene the attachments UI stays permanently
 * empty, so it is hidden rather than shown as an empty state.
 *
 * Note this answers "can attachments still arrive?", NOT "does this scene have
 * attachments?". Scans the app uploaded before it switched to the
 * `from-roomscan` endpoint created their scene with a plain POST /scenes and
 * only then uploaded the attachments, so those scenes carry attachments with
 * no `roomscanId`. Callers deciding whether to *show* existing attachments
 * must check the attachments themselves and use this only as the extra reason
 * to show an empty section (see Details.tsx).
 */
export const isRoomscanScene = (
	scene: Scene | SceneExtended | undefined | null,
): boolean => {
	const roomscanId = scene?.plan?.roomscanId;
	if (!roomscanId) return false;
	return typeof roomscanId === "object"
		? !!(roomscanId as { _id?: string })._id
		: true;
};
